import { IDomEditor, IEditorConfig, IToolbarConfig } from "@wangeditor/editor";
import { Editor, Toolbar } from "@wangeditor/editor-for-react";
import "@wangeditor/editor/dist/css/style.css";
import { marked } from "marked";
import mermaid from "mermaid";
import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Mermaid component to render the diagram
const Mermaid: React.FC<{ chart: string }> = ({ chart }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    mermaid.initialize({ startOnLoad: true });
    if (ref.current) {
      mermaid.run({
        nodes: [ref.current],
      });
    }
  }, [chart]);

  return (
    <div className="mermaid" ref={ref}>
      {chart}
    </div>
  );
};


interface WangEditorMarkdownEditorProps {
  // 初始 Markdown 内容
  defaultValue?: string;
  // 内容变化回调（返回最新 Markdown 字符串）
  onChange?: (markdown: string) => void;
  onStart?: (editor: IDomEditor) => void;
  // 编辑器高度
  height?: number;
  // 是否禁用编辑（由父组件控制）
  disabled?: boolean;
}

const WangEditorMarkdownEditor = React.forwardRef<
  IDomEditor,
  WangEditorMarkdownEditorProps
>(
  (
    { defaultValue = "", onChange, onStart, height = 500, disabled = false },
    ref
  ) => {
    const editorRef = useRef<IDomEditor | null>(null);
    const [editorInstance, setEditorInstance] = useState<IDomEditor | null>(null);
    const [markdown, setMarkdown] = useState<string>(""); // Markdown 内容 (实际存的是HTML)
    const [rawMarkdown, setRawMarkdown] = useState(""); // Raw markdown for preview

    // ==================== 核心逻辑：动态监听 disabled 变化 ====================
    useEffect(() => {
      const editor = editorRef.current;
      if (editor == null) return;

      // 根据传入的 disabled 属性动态切换编辑器状态
      if (disabled) {
        editor.disable();
      } else {
        editor.enable();
      }
    }, [disabled]); // 依赖项包含 disabled
    // ======================================================================

    // 工具栏配置
    const toolbarConfig: Partial<IToolbarConfig> = {
      toolbarKeys: [
        "header1",
        "header2",
        "header3",
        "|",
        "bold",
        "italic",
        "through",
        "underline",
        "|",
        "bulletedList",
        "numberedList",
        "|",
        "blockquote",
        "codeBlock",
        "|",
        "link",
        "image",
        "|",
        "undo",
        "redo",
        "|",
        "fullScreen",
        "|",
        "modeSwitch",
      ],
    };

    /**
     * 转义Markdown特殊字符
     */
    const escapeMarkdown = (str: string): string => {
      return str.replace(/([\\`*_{}()#+\-.!])/g, "\\$1").replace(/\|/g, "\\|");
    };

    /**
     * 将table DOM元素转换为Markdown表格字符串
     */
    const tableToMarkdown = (tableEl: HTMLTableElement): string => {
      if (!tableEl || tableEl.tagName !== "TABLE") return "";

      const rows = Array.from(tableEl.querySelectorAll("tr"));
      if (rows.length === 0) return "";

      const cellData = rows.map((row) => {
        const cells = Array.from(row.querySelectorAll("th, td"));
        return cells
          .map((cell) => {
            const cellElement = cell as HTMLTableCellElement;
            const text = escapeMarkdown(cellElement.textContent?.trim() || "");
            const colspan = cellElement.colSpan || 1;
            const rowspan = cellElement.rowSpan || 1;

            let cellText = text;
            if (colspan > 1 || rowspan > 1) {
              cellText = `${text}[合并${colspan}列${rowspan}行]`;
            }
            return [cellText].concat(Array(colspan - 1).fill("")).flat();
          })
          .flat();
      });

      const maxColCount = Math.max(...cellData.map((row) => row.length), 1);
      const normalizedData = cellData.map((row) => {
        return row
          .concat(Array(maxColCount - row.length).fill(""))
          .slice(0, maxColCount);
      });

      if (normalizedData.every((row) => row.every((cell) => cell === ""))) {
        return "";
      }

      const markdownLines: string[] = [];
      markdownLines.push(`| ${normalizedData[0].join(" | ")} |`);
      markdownLines.push(
        `| ${normalizedData[0].map(() => "-----").join(" | ")} |`
      );
      normalizedData.slice(1).forEach((row) => {
        markdownLines.push(`| ${row.join(" | ")} |`);
      });

      return markdownLines.join("\n");
    };

    /**
     * HTML转Markdown
     */
    const htmlToMarkdown = async (htmlStr: string): Promise<string> => {
      if (!htmlStr) return "";

      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlStr;

      const tableElements = tempDiv.querySelectorAll("table");
      tableElements.forEach((tableEl) => {
        const markdownTable = tableToMarkdown(tableEl as HTMLTableElement);
        const textNode = document.createTextNode(`\n\n${markdownTable}\n\n`);
        tableEl.parentNode?.replaceChild(textNode, tableEl);
      });

      let processedHtml = tempDiv.innerHTML;

      const decodeHtml = (html: string) => {
        const txt = document.createElement("textarea");
        txt.innerHTML = html;
        return txt.value;
      };

      const cleanHtml = processedHtml
        .replace(/>\s+</g, '><')
        .replace(/<p\s*\/?>/g, "")
        .replace(/<\/p>/g, "  \n")
        .replace(/<br\s*\/?>/g, "  \n")
        .replace(/<strong>(.*?)<\/strong>/g, "**$1**")
        .replace(/<em>(.*?)<\/em>/g, "*$1*")
        .replace(/<h1>(.*?)<\/h1>/g, "\n\n# $1\n\n")
        .replace(/<h2>(.*?)<\/h2>/g, "\n\n## $1\n\n")
        .replace(/<h3>(.*?)<\/h3>/g, "\n\n### $1\n\n")
        .replace(/<a\s+href="(.*?)"[^>]*>(.*?)<\/a>/g, "[$2]($1)")
        .replace(/<ul\s*\/?>/g, "\n\n")
        .replace(/<\/ul>/g, "\n\n")
        .replace(/<li>(.*?)<\/li>(?![\s\S]*<ol>)/g, "- $1\n")
        .replace(/<ol\s*\/?>/g, "\n\n")
        .replace(/<\/ol>/g, "\n\n")
        .replace(/<li>(.*?)<\/li>(?![\s\S]*<ul>)/g, "1. $1\n")
        .replace(/<code>(.*?)<\/code>/g, "`$1`")
        .replace(
          /<pre[^>]*><code[^>]*class="[^"]*language-([^"\s]+)[^"]*"[^>]*>([\s\S]*?)<\/code><\/pre>/g,
          (_match, lang, code) => {
            return `\n\`\`\`${lang}\n${decodeHtml(code)}\n\`\`\`\n`;
          }
        )
        .replace(
          /<pre[^>]*><code[^>]*>([\s\S]*?)<\/code><\/pre>/g,
          (_match, code) => {
            return `\n\`\`\`\n${decodeHtml(code)}\n\`\`\`\n`;
          }
        )
        .replace(/<pre[^>]*>([\s\S]*?)<\/pre>/g, (_match, code) => {
          return `\n\`\`\`\n${decodeHtml(code)}\n\`\`\`\n`;
        })
        .replace(/<img\s+src="(.*?)"\s+alt="(.*?)"[^>]*\/?>/g, "![$2]($1)")
        .replace(/<img\s+src="(.*?)"[^>]*\/?>/g, "![]($1)")
        .replace(/<[^>]*>/g, "")
        .replace(/\n{3,}/g, "\n\n")
        .trim();

      return cleanHtml;
    };

    const [isFocused, setIsFocused] = useState(false);

    // 编辑器核心配置
    const editorConfig: Partial<IEditorConfig> = {
      readOnly: disabled, // 初始化时的只读状态
      autoFocus: !disabled, // 禁用时不自动聚焦
      onFocus: (editor: IDomEditor) => {
        setIsFocused(true);
      },
      onBlur: (editor: IDomEditor) => {
        setIsFocused(false);
      },
      onChange: async (editor: IDomEditor) => {
        if (onChange) {
          let html = editor.getHtml();

          html = html.replace(/<img(.*?)data-href=".*?"(.*?)>/g, "<img$1$2>");

          html = html.replace(
            /<img(.*?)style=".*?"(.*?)>/g,
            (match, p1, p2) => {
              return `<img${p1} ${p2}>`;
            }
          );

          const markdown = await htmlToMarkdown(html);
          setRawMarkdown(markdown);
          onChange(markdown);
        }
      },
    };

    // 初始化：设置初始 Markdown 内容
    useEffect(() => {
      // 移除 setMarkdown(defaultValue); 防止闪烁
      const initMarkdown = async () => {
        try {
          const marks = await marked.parse(defaultValue);
          console.log(marks, 'marks');

          if (editorRef.current) {
            console.log('daaass');
            try {
              editorRef.current.setHtml(marks);
            } catch (e) {
              console.error("setHtml error:", e);
            }
          }
          setRawMarkdown(defaultValue);
        } catch (error) {
          console.error("Markdown解析失败:", error);
        }
      };

      if (defaultValue) {
        initMarkdown();
      }
    }, [defaultValue]);

    // 销毁编辑器
    useEffect(() => {
      return () => {
        if (editorRef.current) {
          editorRef.current.destroy();
          editorRef.current = null;
        }
      };
    }, []);

    return (
      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: "4px",
          backgroundColor: "#fff",
          position: "relative",
        }}
      >
        <div
          style={{
            position: disabled || !isFocused ? "absolute" : "relative",
            visibility: disabled || !isFocused ? "hidden" : "visible",
            pointerEvents: disabled || !isFocused ? "none" : "auto",
            zIndex: disabled || !isFocused ? -1 : 1,
            width: "100%",
          }}
        >
          <Toolbar
            editor={editorInstance}
            defaultConfig={toolbarConfig}
            mode="default"
            style={{ borderBottom: "1px solid #e5e7eb" }}
          />
        </div>

        {/* Render editor always to keep Toolbar instance valid, but hide via CSS if disabled OR blurred */}
        <div
          style={{
            position: disabled || !isFocused ? "absolute" : "relative",
            visibility: disabled || !isFocused ? "hidden" : "visible",
            pointerEvents: disabled || !isFocused ? "none" : "auto",
            zIndex: disabled || !isFocused ? -1 : 1,
            width: "100%",
          }}
        >
          <Editor
            defaultConfig={editorConfig}
            value={markdown}
            mode="default"
            onCreated={(editor) => {
              editorRef.current = editor;
              setEditorInstance(editor);

              // 处理 Ref 转发
              if (typeof ref === "function") {
                ref(editor);
              } else if (ref) {
                (ref as React.MutableRefObject<IDomEditor | null>).current =
                  editor;
              }

              // 确保创建时的状态正确（虽然 editorConfig 处理了，但双重保障更稳妥）
              if (disabled) {
                editor.disable();
              }

              onStart?.(editor);
            }}
            style={{ height: `${height}px`, overflow: "auto" }}
          />
        </div>

        {/* Render Preview overlay when disabled OR blurred */}
        {(disabled || !isFocused) && (
          <div
            className="react-markdown-preview-toggle"
            onClick={() => {
              if (!disabled) {
                setIsFocused(true);
                setTimeout(() => {
                  try {
                    editorRef.current?.enable();
                    editorRef.current?.focus();
                  } catch (e) { }
                }, 10);
              }
            }}
            style={{
              height: `${height}px`,
              overflow: "auto",
              padding: "20px",
              backgroundColor: "#fff",
              cursor: disabled ? "default" : "text",
            }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                code(props) {
                  const { children, className, node, ...rest } = props;
                  const match = /language-(\w+)/.exec(className || "");
                  const codeString = String(children).replace(/\n$/, "");
                  const isMermaid = match?.[1] === "mermaid" || /^(sequenceDiagram|flowchart|graph|pie|gantt|classDiagram|stateDiagram|erDiagram|journey)\b/i.test(codeString.trim());

                  if (match && match[1] === "mermaid" || isMermaid) {
                    return <Mermaid chart={codeString} />;
                  }

                  return (
                    <code {...rest} className={className}>
                      {children}
                    </code>
                  );
                },
              }}
            >
              {rawMarkdown || defaultValue}
            </ReactMarkdown>
          </div>
        )}
      </div>
    );
  }
);

export default WangEditorMarkdownEditor;
